import { useState, useEffect } from 'react';
import { DonutChart } from './DonutChart';
import { TallyBarChart } from './TallyBarChart';
import { getChartsDataSubject, ChartsData, ChartData, fetchChartsData } from '../../../store/Charts';

export const DashboardCharts = () => {
  const [chartsData, setChartsData] = useState<ChartsData>({
    openBnCValue: [],
    overdueFollowUp: [],
    overdueTicket: [],
    validationNeeded: [],
    missingLink: [],
    disconnectedNotDecommissioned: [],
    originalStillCommissioning: [],
    openBnCByAP: [],
    openBnCByMonth: [],
    originalAndRenewalOverlap: []
  });
  const [labelColorMap, setLabelColorMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);

  const colorScale = [
    '#D96E2B',
    '#5C85E5',
    '#9EA688',
    '#B8D936',
    '#8777D9',
    '#DABD2C',
    '#FCCE7E',
    '#3F527F',
    '#4B39A2',
    '#F5E389'
  ];

  useEffect(() => {
    const chartsDataSubscription = getChartsDataSubject().subscribe((data) => {
      if (data) {
        setChartsData(data);
        setLoading(false);
      }
    });

    fetchChartsData();

    return () => {
      if (chartsDataSubscription) chartsDataSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (Object.keys(labelColorMap).length > 0 || loading || !chartsData) return;

    const uniqueLabels = new Set<string>();
    Object.values(chartsData).forEach((dataArray) => {
      dataArray.forEach((item: ChartData) => uniqueLabels.add(item.label));
    });
    const newLabelColorMap: Record<string, string> = {};
    let colorIndex = 0;
    uniqueLabels.forEach((label) => {
      if (!labelColorMap[label]) {
        newLabelColorMap[label] = colorScale[colorIndex % colorScale.length];
        colorIndex++;
      }
    });
    setLabelColorMap(newLabelColorMap);
  }, [chartsData]);

  const chartOrder = [
    'openBnCValue',
    'overdueFollowUp',
    'overdueTicket',
    'validationNeeded',
    'missingLink',
    'disconnectedNotDecommissioned',
    'originalStillCommissioning',
    'originalAndRenewalOverlap'
  ];

  const titleByKeyMap: Record<string, string> = {
    openBnCValue: 'Open BnC Value',
    overdueFollowUp: 'Overdue Follow-Ups',
    overdueTicket: 'Overdue Tickets',
    validationNeeded: 'Validation Needed',
    missingLink: 'Missing Link',
    disconnectedNotDecommissioned: 'Disconnected Not Decommissioned',
    originalStillCommissioning: 'Original Still Commissioning',
    openBnCByAP: 'Open BnC By AP',
    openBnCByMonth: 'Open BnC By Month',
    originalAndRenewalOverlap: 'Original and Renewal Overlap'
  };

  return (
    <div className="my-5">
      <div className="flex flex-col gap-4">
        <TallyBarChart
          data={chartsData.openBnCByAP}
          loading={loading}
          title="Open BnC By AP"
          filterField="salesAgent"
          filterOperator="equals"
        />
        <TallyBarChart
          data={chartsData.openBnCByMonth}
          loading={loading}
          title="Open BnC By Month"
          filterField="expectedFirstCommissionRun"
          filterOperator="between"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-2 gap-y-6">
        {chartOrder.map((chartKey) => {
          const data =
            chartsData[chartKey as keyof typeof chartsData]?.map((item) => ({
              ...item,
              color: labelColorMap[item.label]
            })) || [];
          return (
            <DonutChart
              data={data}
              key={chartKey}
              loading={loading}
              title={titleByKeyMap[chartKey as keyof typeof titleByKeyMap]}
            />
          );
        })}
      </div>
    </div>
  );
};
